import { createElement } from 'react';
import {
  IconBoard,
  IconBulletList,
  IconCalendarMark,
  IconFileBookmark,
  IconLayoutWindow25,
} from '../../icons/index.js';
import type { ViewType } from '../contract.js';

/**
 * LES CINQ VUES NATIVES DU CORE.
 *
 * Ce module est expose en SOUS-CHEMIN (`@taki/ui/views/core`), volontairement
 * HORS du barrel `@taki/ui` : sinon le moindre ecran embarquerait les cinq
 * vues. Un ecran importe explicitement celles qu'il propose :
 *
 *   import { liste, fiche, tableau } from '@taki/ui/views/core';
 *
 * Les vues ne s'auto-enregistrent JAMAIS : sans cet import explicite, le build
 * les supprimerait (elles ne seraient referencees par personne).
 *
 * Chaque descripteur declare HONNETEMENT ce dont il a besoin, meme si le lot 1
 * charge tout cote client :
 * - `requires` : les roles sans lesquels l'entree du selecteur est grisee, avec
 *   sa raison au survol (jamais absente). Le pseudo-role `columns` exige au
 *   moins une colonne declaree.
 * - `demand` : ce que la vue exigera du chargeur le jour ou le SDK paginera.
 *   C'est ce champ qui evitera de rouvrir les cinq vues a ce moment-la.
 * - `scroll` : qui possede la barre de defilement, la zone 5 ou la vue.
 *
 * Le SQUELETTE n'est pas declare ici : il voyage dans le module de la vue
 * (export `Skeleton`, a cote de `Component`), et le `ViewHost` le lit une fois
 * `load()` resolu. Le declarer sur le descripteur obligerait a importer le
 * fichier de la vue en statique, et `load()` ne serait plus paresseux. Une vue
 * sans squelette propre (la liste) garde celui du gabarit.
 *
 * Les icones viennent du registre EXISTANT de @taki/ui : aucun SVG maison.
 *
 * Ce fichier n'ecrit pas de JSX (d'ou `createElement`) pour rester un `.ts` :
 * c'est un fichier de declaration, pas un fichier de rendu.
 */

/** Taille d'icone du selecteur, alignee sur les icones d'action de la zone 6. */
const ICON_SIZE = 15;

/**
 * Liste : reconnaitre. Le repli universel, la seule vue qui ne peut jamais
 * etre grisee des que l'ecran declare un titre.
 */
export const liste: ViewType = {
  id: 'core.liste',
  label: 'Liste',
  icon: createElement(IconBulletList, { size: ICON_SIZE }),
  level: 'collection',
  requires: ['title'],
  // Ligne a hauteur fixe : la liste sait n'afficher qu'une fenetre du jeu.
  demand: 'window',
  scroll: 'zone',
  load: () => import('./ListView.js'),
};

/**
 * Fiche : consulter et agir sur UN element. Second groupe du selecteur, apres
 * le filet : le niveau se voit a l'ecran.
 */
export const fiche: ViewType = {
  id: 'core.fiche',
  label: 'Fiche',
  icon: createElement(IconFileBookmark, { size: ICON_SIZE }),
  level: 'record',
  requires: ['title'],
  // Un element : la plus petite demande possible au chargeur.
  demand: 'page',
  scroll: 'zone',
  load: () => import('./RecordView.js'),
};

/** Tableau : comparer des valeurs. Sans colonne declaree, il n'a rien a montrer. */
export const tableau: ViewType = {
  id: 'core.tableau',
  label: 'Tableau',
  icon: createElement(IconLayoutWindow25, { size: ICON_SIZE }),
  level: 'collection',
  requires: ['title', 'columns'],
  // Pagination classique : 200 lignes par page, premiere colonne gelee.
  demand: 'page',
  // Defilement horizontal et colonne gelee : la vue possede ses barres.
  scroll: 'self',
  load: () => import('./TableView.js'),
};

/**
 * Kanban : faire avancer un dossier a la souris. Exige `groupKey` ET son
 * catalogue (`options()`), sinon il fabrique des colonnes fantomes.
 */
export const kanban: ViewType = {
  id: 'core.kanban',
  label: 'Kanban',
  icon: createElement(IconBoard, { size: ICON_SIZE }),
  level: 'collection',
  requires: ['title', 'groupKey'],
  // Une carte mal placee est un mensonge : le kanban doit voir tout le jeu
  // courant pour repartir chaque carte dans sa colonne.
  demand: 'all',
  // Chaque colonne defile pour elle-meme.
  scroll: 'self',
  load: () => import('./KanbanView.js'),
};

/** Calendrier : placer dans le temps. Charge par INTERVALLE, pas par page. */
export const calendrier: ViewType = {
  id: 'core.calendrier',
  label: 'Calendrier',
  icon: createElement(IconCalendarMark, { size: ICON_SIZE }),
  level: 'collection',
  requires: ['title', 'dateStart'],
  // Une fenetre de dates, deplacee par `onRangeChange`.
  demand: 'window',
  // Grille de mois a hauteur fixe : la vue gere son cadre.
  scroll: 'self',
  load: () => import('./CalendarView.js'),
};

/**
 * Le catalogue complet, dans l'ordre du selecteur : les vues collection, puis
 * la fiche. Un ecran qui les veut toutes passe `types: coreViews`.
 */
export const coreViews: ViewType[] = [liste, tableau, kanban, calendrier, fiche];
